import { createBusinessCenterOperationalStateStore } from "../../lib/businessCenterOperationalStateStore";

export type ARCustomerStatus = "Prospect" | "Onboarding" | "Active" | "Inactive" | "Suspended" | "Closed" | "Archived" | "Transferred" | "Merged";
export type ARAccountStatus = "Prospect" | "Open" | "Active" | "Active Recovery" | "Payment Plan" | "Inactive" | "Suspended" | "Disputed" | "Closed" | "Archived" | "Transferred" | "Merged";
export type ARAgingBucket = "Current" | "1-30 Days" | "31-60 Days" | "61-90 Days" | "90+ Days";

export interface ARCustomerContact {
  id: string;
  name: string;
  title: string;
  email: string;
  phone: string;
  preferredContactMethod: "Email" | "Phone" | "Email + Phone";
  notes: string;
}

export interface ARAddress {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface ARHierarchyTimelineEvent {
  id: string;
  type: "Customer Created" | "Account Created" | "Status Change" | "Contact Change" | "Assignment Change" | "Note" | "Major Event";
  detail: string;
  actorName: string;
  createdAt: string;
}

export interface ARInternalNote {
  id: string;
  body: string;
  actorName: string;
  actorEmail: string;
  createdAt: string;
  internalOnly: true;
}

export interface ARCustomerRecord {
  id: string;
  clientId: string;
  customerName: string;
  customerNumber: string;
  companyType: string;
  industry: string;
  website: string;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  primaryContactPreferredContactMethod: "Email" | "Phone" | "Email + Phone";
  accountsPayableContactName: string;
  accountsPayableContactEmail: string;
  accountsPayableContactPhone: string;
  controllerContactName: string;
  controllerContactEmail: string;
  controllerContactPhone: string;
  cfoContactName: string;
  cfoContactEmail: string;
  cfoContactPhone: string;
  status: ARCustomerStatus;
  contacts: ARCustomerContact[];
  addresses: {
    billing: ARAddress;
    shipping: ARAddress;
    headquarters: ARAddress;
  };
  internalNotes: string;
  communicationHistoryPlaceholder: string;
  timeline: ARHierarchyTimelineEvent[];
  notes: ARInternalNote[];
  createdAt: string;
  updatedAt: string;
}

export interface ARAccountRecord {
  id: string;
  clientId: string;
  customerId: string;
  accountNumber: string;
  clientReference: string;
  customerReference: string;
  status: ARAccountStatus;
  currentBalanceCents: number;
  agingBucket: ARAgingBucket;
  creditLimitCents: number | null;
  lastPaymentDate: string | null;
  lastPaymentAmountCents: number;
  assignedCollectorUserId: string;
  assignedManagerUserId: string;
  promiseStatus: "None" | "Open" | "Due" | "Broken" | "Completed";
  holdStatus: "None" | "Internal Hold" | "Client Hold";
  disputeStatus: "None" | "Open" | "Under Review" | "Resolved";
  riskLevel: "Low" | "Medium" | "High" | "Critical";
  lastContactAt: string | null;
  nextAction: string;
  recoveryTimelinePlaceholder: string;
  recoveryInstructions: string;
  notes: ARInternalNote[];
  timeline: ARHierarchyTimelineEvent[];
  createdAt: string;
  updatedAt: string;
}

export type ARInvoiceStatus = "Draft" | "Open" | "Past Due" | "Partial" | "Paid" | "On Hold" | "Disputed" | "Cancelled";

export interface ARInvoiceRecord {
  id: string;
  clientId: string;
  customerId: string;
  accountId: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  originalAmountCents: number;
  currentBalanceCents: number;
  status: ARInvoiceStatus;
  purchaseOrder: string;
  referenceNumber: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface ARCustomerAccountState {
  customers: ARCustomerRecord[];
  accounts: ARAccountRecord[];
  invoices: ARInvoiceRecord[];
}

const COMPANY_ID = "company-future-ar-recovery";

const hierarchyStore = createBusinessCenterOperationalStateStore<ARCustomerAccountState>({
  moduleKey: "ar_recovery_customer_accounts",
  scope: { scopeType: "company", companyId: COMPANY_ID },
  seed: () => ({ customers: [], accounts: [], invoices: [] }),
  normalize: normalizeState,
});

export function loadARCustomerAccountState() {
  return hierarchyStore.read();
}

export function refreshARCustomerAccountState() {
  return hierarchyStore.refresh();
}

export function saveARCustomerAccountState(state: ARCustomerAccountState) {
  return hierarchyStore.writeAsync(state);
}

export async function upsertARCustomer(customer: ARCustomerRecord) {
  const current = loadARCustomerAccountState();
  const normalized = normalizeCustomer({ ...customer, updatedAt: new Date().toISOString() });
  const duplicate = current.customers.find(
    (item) =>
      item.id !== normalized.id &&
      item.clientId === normalized.clientId &&
      normalizeComparableText(item.customerName) === normalizeComparableText(normalized.customerName),
  );
  if (duplicate) throw new Error(`Customer "${normalized.customerName}" already exists for this client.`);
  const exists = current.customers.some((item) => item.id === normalized.id);
  return saveARCustomerAccountState({
    ...current,
    customers: exists ? current.customers.map((item) => item.id === normalized.id ? normalized : item) : [normalized, ...current.customers],
  });
}

export async function upsertARAccount(account: ARAccountRecord) {
  const current = loadARCustomerAccountState();
  const normalized = normalizeAccount({ ...account, updatedAt: new Date().toISOString() });
  const parent = current.customers.find((customer) => customer.id === normalized.customerId);
  if (!parent || parent.clientId !== normalized.clientId) throw new Error("Account hierarchy is invalid. Select a customer under the chosen client.");
  const duplicate = current.accounts.find(
    (item) =>
      item.id !== normalized.id &&
      item.clientId === normalized.clientId &&
      item.customerId === normalized.customerId &&
      normalizeComparableText(item.accountNumber) === normalizeComparableText(normalized.accountNumber),
  );
  if (duplicate) throw new Error(`Account "${normalized.accountNumber}" already exists for this customer.`);
  const exists = current.accounts.some((item) => item.id === normalized.id);
  return saveARCustomerAccountState({
    ...current,
    accounts: exists ? current.accounts.map((item) => item.id === normalized.id ? normalized : item) : [normalized, ...current.accounts],
  });
}

export async function upsertARInvoice(invoice: ARInvoiceRecord) {
  const current = loadARCustomerAccountState();
  const normalized = normalizeInvoice({ ...invoice, updatedAt: new Date().toISOString() });
  const parent = current.accounts.find((account) => account.id === normalized.accountId);
  if (!parent || parent.clientId !== normalized.clientId || parent.customerId !== normalized.customerId) throw new Error("Invoice hierarchy is invalid. Select an account under the chosen client and customer.");
  const duplicate = current.invoices.find(
    (item) =>
      item.id !== normalized.id &&
      item.clientId === normalized.clientId &&
      item.accountId === normalized.accountId &&
      normalizeComparableText(item.invoiceNumber) === normalizeComparableText(normalized.invoiceNumber),
  );
  if (duplicate) throw new Error(`Invoice "${normalized.invoiceNumber}" already exists for this account.`);
  const exists = current.invoices.some((item) => item.id === normalized.id);
  return saveARCustomerAccountState({
    ...current,
    invoices: exists ? current.invoices.map((item) => (item.id === normalized.id ? normalized : item)) : [normalized, ...current.invoices],
  });
}

export function createHierarchyId(prefix: "customer" | "account" | "contact" | "event" | "note" | "invoice") {
  return `ar-${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function emptyAddress(): ARAddress {
  return { line1: "", line2: "", city: "", state: "", postalCode: "", country: "United States" };
}

function normalizeState(state: ARCustomerAccountState): ARCustomerAccountState {
  return {
    customers: Array.isArray(state?.customers) ? state.customers.map(normalizeCustomer) : [],
    accounts: Array.isArray(state?.accounts) ? state.accounts.map(normalizeAccount) : [],
    invoices: Array.isArray(state?.invoices) ? state.invoices.map(normalizeInvoice) : [],
  };
}

function normalizeCustomer(customer: ARCustomerRecord): ARCustomerRecord {
  const now = new Date().toISOString();
  const address = (value?: ARAddress): ARAddress => ({ ...emptyAddress(), ...(value || {}) });
  const primary = customer.contacts?.[0];
  const ap = customer.contacts?.[1];
  const controller = customer.contacts?.[2];
  const cfo = customer.contacts?.[3];
  return {
    ...customer,
    clientId: customer.clientId || "",
    customerName: customer.customerName?.trim() || "Untitled Customer",
    customerNumber: customer.customerNumber?.trim() || "",
    companyType: customer.companyType?.trim() || "",
    industry: customer.industry?.trim() || "",
    website: customer.website?.trim() || "",
    primaryContactName: customer.primaryContactName?.trim() || primary?.name?.trim() || "",
    primaryContactEmail: customer.primaryContactEmail?.trim() || primary?.email?.trim() || "",
    primaryContactPhone: customer.primaryContactPhone?.trim() || primary?.phone?.trim() || "",
    primaryContactPreferredContactMethod: customer.primaryContactPreferredContactMethod || primary?.preferredContactMethod || "Email",
    accountsPayableContactName: customer.accountsPayableContactName?.trim() || ap?.name?.trim() || "",
    accountsPayableContactEmail: customer.accountsPayableContactEmail?.trim() || ap?.email?.trim() || "",
    accountsPayableContactPhone: customer.accountsPayableContactPhone?.trim() || ap?.phone?.trim() || "",
    controllerContactName: customer.controllerContactName?.trim() || controller?.name?.trim() || "",
    controllerContactEmail: customer.controllerContactEmail?.trim() || controller?.email?.trim() || "",
    controllerContactPhone: customer.controllerContactPhone?.trim() || controller?.phone?.trim() || "",
    cfoContactName: customer.cfoContactName?.trim() || cfo?.name?.trim() || "",
    cfoContactEmail: customer.cfoContactEmail?.trim() || cfo?.email?.trim() || "",
    cfoContactPhone: customer.cfoContactPhone?.trim() || cfo?.phone?.trim() || "",
    status: normalizeCustomerStatus(customer.status),
    contacts: Array.isArray(customer.contacts) ? customer.contacts : [],
    addresses: {
      billing: address(customer.addresses?.billing),
      shipping: address(customer.addresses?.shipping),
      headquarters: address(customer.addresses?.headquarters),
    },
    internalNotes: customer.internalNotes?.trim() || "",
    communicationHistoryPlaceholder: customer.communicationHistoryPlaceholder?.trim() || "Communication history will appear here once outreach and service workflows are connected.",
    timeline: Array.isArray(customer.timeline) ? customer.timeline : [],
    notes: Array.isArray(customer.notes) ? customer.notes : [],
    createdAt: customer.createdAt || now,
    updatedAt: customer.updatedAt || now,
  };
}

function normalizeAccount(account: ARAccountRecord): ARAccountRecord {
  const now = new Date().toISOString();
  return {
    ...account,
    clientId: account.clientId || "",
    customerId: account.customerId || "",
    accountNumber: account.accountNumber?.trim() || "",
    clientReference: account.clientReference?.trim() || "",
    customerReference: account.customerReference?.trim() || "",
    status: normalizeAccountStatus(account.status),
    currentBalanceCents: Number.isFinite(account.currentBalanceCents) ? account.currentBalanceCents : 0,
    agingBucket: ["Current", "1-30 Days", "31-60 Days", "61-90 Days", "90+ Days"].includes(account.agingBucket) ? account.agingBucket : "Current",
    creditLimitCents: Number.isFinite(account.creditLimitCents) ? account.creditLimitCents : null,
    lastPaymentDate: account.lastPaymentDate || null,
    lastPaymentAmountCents: Number.isFinite(account.lastPaymentAmountCents) ? account.lastPaymentAmountCents : 0,
    assignedCollectorUserId: account.assignedCollectorUserId || "",
    assignedManagerUserId: account.assignedManagerUserId || "",
    promiseStatus: ["None", "Open", "Due", "Broken", "Completed"].includes(account.promiseStatus) ? account.promiseStatus : "None",
    holdStatus: ["None", "Internal Hold", "Client Hold"].includes(account.holdStatus) ? account.holdStatus : "None",
    disputeStatus: ["None", "Open", "Under Review", "Resolved"].includes(account.disputeStatus) ? account.disputeStatus : "None",
    riskLevel: ["Low", "Medium", "High", "Critical"].includes(account.riskLevel) ? account.riskLevel : "Medium",
    lastContactAt: account.lastContactAt || null,
    nextAction: account.nextAction?.trim() || "",
    recoveryTimelinePlaceholder: account.recoveryTimelinePlaceholder?.trim() || "Recovery timeline will populate when operations begin.",
    recoveryInstructions: account.recoveryInstructions || "",
    notes: Array.isArray(account.notes) ? account.notes : [],
    timeline: Array.isArray(account.timeline) ? account.timeline : [],
    createdAt: account.createdAt || now,
    updatedAt: account.updatedAt || now,
  };
}

function normalizeInvoice(invoice: ARInvoiceRecord): ARInvoiceRecord {
  const now = new Date().toISOString();
  return {
    ...invoice,
    clientId: invoice.clientId || "",
    customerId: invoice.customerId || "",
    accountId: invoice.accountId || "",
    invoiceNumber: invoice.invoiceNumber?.trim() || "",
    invoiceDate: invoice.invoiceDate || now,
    dueDate: invoice.dueDate || now,
    originalAmountCents: Number.isFinite(invoice.originalAmountCents) ? invoice.originalAmountCents : 0,
    currentBalanceCents: Number.isFinite(invoice.currentBalanceCents) ? invoice.currentBalanceCents : 0,
    status: ["Draft", "Open", "Past Due", "Partial", "Paid", "On Hold", "Disputed", "Cancelled"].includes(invoice.status) ? invoice.status : "Draft",
    purchaseOrder: invoice.purchaseOrder?.trim() || "",
    referenceNumber: invoice.referenceNumber?.trim() || "",
    notes: invoice.notes?.trim() || "",
    createdAt: invoice.createdAt || now,
    updatedAt: invoice.updatedAt || now,
  };
}

function normalizeCustomerStatus(status: string): ARCustomerStatus {
  if (status === "On Hold") return "Suspended";
  if (status === "Resolved") return "Closed";
  return ["Prospect", "Onboarding", "Active", "Inactive", "Suspended", "Closed", "Archived", "Transferred", "Merged"].includes(status)
    ? (status as ARCustomerStatus)
    : "Onboarding";
}

function normalizeAccountStatus(status: string): ARAccountStatus {
  if (status === "On Hold") return "Suspended";
  if (status === "Resolved") return "Closed";
  return ["Prospect", "Open", "Active", "Active Recovery", "Payment Plan", "Inactive", "Suspended", "Disputed", "Closed", "Archived", "Transferred", "Merged"].includes(status)
    ? (status as ARAccountStatus)
    : "Open";
}

function normalizeComparableText(value: string) {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}
